import { defineStore } from 'pinia'
import axios from 'axios'
import { API } from '@/utils/http'
import { toast } from '@/components/ui/toast/use-toast'
import { useAccessStore } from '@/stores/access'
import { useOrgStore } from '@/stores/org'

const HOST = '/api/3'

type ProjectId = number | string
// Project payloads carry more than types/Project (children, monitoring_enabled, ...).
type ProjectItem = any

const selectedOrgId = () => useOrgStore().selectedOrg || useOrgStore().current?.organization_id

interface ProjectState {
  current: ProjectItem | null
  lastSelected: any
  projects: ProjectItem[]
  flatedProjects: ProjectItem[]
  allTags: any[]
  components: any[] | null
  allComponents: any[] | null
  invites: any[]
  allInvites: any[]
  currentMember: any
  members: any[]
  allMembers: any[]
  membersCount: number
  planetFeatures: any
  isLoading: boolean
  joinableProjects: ProjectItem[]
}

export const useProjectStore = defineStore('project', {
  state: (): ProjectState => ({
    current: null,
    lastSelected: null,
    projects: [],
    flatedProjects: [],
    allTags: [],
    components: null,
    allComponents: null,
    invites: [],
    allInvites: [],
    currentMember: {},
    members: [],
    allMembers: [],
    membersCount: 0,
    planetFeatures: null,
    isLoading: false,
    joinableProjects: [],
  }),

  getters: {
    getJoinableProjects: (state): ProjectItem[] => {
      const orgId = parseInt(useOrgStore().selectedOrg as string)
      return state.joinableProjects.filter(
        p => p.organization_id === orgId && !state.flatedProjects.some(m => m.project_id === p.project_id)
      )
    },
    isMonitoringEnabled: state => (projectId: ProjectId | null | undefined) => {
      const id = parseInt(projectId as string)
      if (!id) return true

      if (state.current && state.current.project_id === id) {
        return !!state.current.monitoring_enabled
      }

      const project = (state.flatedProjects || []).find(p => p.project_id === id)
      // Fail open for projects we have no payload for — the API is the authority
      // and rejects unmonitorable projects with a warning header.
      return project ? !!project.monitoring_enabled : true
    },
  },

  actions: {
    setProjects(projects: ProjectItem[]) {
      this.projects = projects
    },
    setCurrent(project: ProjectItem | null) {
      this.current = project
      localStorage.setItem('selectedProject', JSON.stringify(project))
    },
    setLastSelected(project: any) {
      this.lastSelected = project
    },
    setAllInvites(invites: any[]) {
      this.allInvites = invites
    },
    removeMemberInvite(uuid: string) {
      this.allInvites = this.allInvites.filter(i => i.uuid !== uuid)
    },
    removeProjectLocal(project: ProjectItem) {
      this.projects = this.projects.filter(p => p.project_id !== project.project_id)
      this.flatedProjects = this.flatedProjects.filter(p => p.project_id !== project.project_id)
    },
    addProjectLocal(project: ProjectItem) {
      this.projects.push(project)
      this.flatedProjects.push(project)
    },
    setComponents(components: any[]) {
      this.components = components.map((component) => {
        const transformedName = component.name
          .split('-')
          .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
          .join('')
        return { ...component, componentName: transformedName }
      })
    },

    // --- Project members -----------------------------------------------------------

    async fetchMembers(projectId: ProjectId) {
      // Try to get orgId from multiple sources
      const orgId = selectedOrgId() || this.current?.organization_id

      if (!orgId) {
        return
      }

      try {
        this.members = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/${projectId}/members`
        }).then(r => r.data)
      } catch (e) {
        toast({ title: "Can't fetch members", variant: 'destructive' })
      }
    },

    async setMemberRole({ member, roleId }: { member: any, roleId: number }) {
      try {
        await useAccessStore().assignProjectRole({projectId: member.project_id, memberId: member.member_id, roleId})
        const current = this.members.find(m => m.member_id === member.member_id)
        const updated = {...(current ?? member), role_id: roleId}
        this.members = this.members.map(m => m.member_id === updated.member_id ? updated : m)
        toast({ title: 'Role changed' })
        return true
      } catch (e) {
        toast({ title: "Couldn't change the role. Reload and try again.", variant: 'destructive' })
        return false
      }
    },

    async fetchAllMembers() {
      const orgId = selectedOrgId() || this.current?.organization_id

      if (!orgId) {
        return
      }

      try {
        this.allMembers = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/members`
        }).then(r => r.data)
      } catch (e: any) {
        throw new Error(e)
      }
    },

    async deleteMember(payload: { projectId: ProjectId, memberId: number }) {
      const orgId = selectedOrgId() || this.current?.organization_id

      if (!orgId) {
        return false
      }

      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/org/${orgId}/project/${payload.projectId}/member/${payload.memberId}`,
        })

        this.members = this.members.filter(m => m.project_member_id !== payload.memberId)
        toast({ title: 'Member deleted' })
        return true
      } catch (e: any) {
        const detail = e.response?.data?.detail
        toast({ title: detail ? `Can't delete member. ${detail}` : "Can't delete member", variant: 'destructive' })
        return false
      }
    },

    // --- Invites -------------------------------------------------------------------

    async fetchInvites(projectId: ProjectId) {
      const orgId = selectedOrgId()

      if (!orgId) {
        return
      }

      try {
        this.invites = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/${projectId}/invites`
        }).then(r => r.data)
      } catch (e) {
        toast({ title: "Can't fetch invites", variant: 'destructive' })
      }
    },

    async addInvite(payload: Record<string, any>) {
      const orgId = payload.organization_id || useOrgStore().selectedOrg || this.current?.organization_id
      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/org/${orgId}/invite`,
          data: {
            inviter_id: payload.inviter_id,
            project_id: payload.project_id,
            email: payload.email,
            phone: payload.phone,
            fullname: payload.fullname || payload.name || '',
            first_name: payload.first_name,
            last_name: payload.last_name,
            role_id: payload.role_id,
            join_project_ids: payload.join_project_ids || [],
            app_only: payload.app_only
          }
        })
        this.invites.push(response.data)
        return response.data ? response.data : null
      } catch (e: any) {
        const data = e.response?.data
        const reason = data?.message ?? (typeof data?.detail === 'string' ? data.detail : '')
        toast({ title: `Can't send invite. ${reason}`.trim(), variant: 'destructive' })
      }
    },

    async deleteInvite(uuid: string) {
      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/invite/${uuid}`
        })

        this.invites = this.invites.filter(i => i.uuid !== uuid)
        toast({ title: 'Invite removed' })
        return true
      } catch (e: any) {
        const detail = e.response?.data?.detail
        toast({ title: detail ? `Can't remove invite. ${detail}` : "Can't remove invite", variant: 'destructive' })
        return false
      }
    },

    async resendInvite(uuid: string) {
      try {
        await API({
          method: 'GET',
          url: `${HOST}/invite/${uuid}/resend`
        })
        toast({ title: 'Invite resent' })

        // Refresh invites list to show updated status
        if (this.current?.project_id) {
          this.fetchInvites(this.current.project_id)
        }
      } catch (e) {
        toast({ title: 'Can\'t resend invite', variant: 'destructive' })
      }
    },

    async fetchAllInvites(payload?: { silent?: boolean }) {
      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/invite`
        })
        this.allInvites = response.data
      } catch (e) {
        if (payload?.silent) return
        toast({ title: 'Can\'t fetch invites', variant: 'destructive' })
      }
    },

    async fetchAllInvitesFirebase(payload: { firebaseToken: string | null }) {
      try {
        const response = await axios({
          method: 'POST',
          url: `${HOST}/invite/firebase`,
          data: {
            firebase_token: payload.firebaseToken
          }
        })
        this.allInvites = response.data
      } catch (e) {
        toast({ title: 'Can\'t fetch invites', variant: 'destructive' })
      }
    },

    async acceptInviteFirebase(payload: { invite: { uuid: string }, firebaseToken: string | null }) {
      const uuid = payload.invite.uuid
      try {
        await axios({
          method: 'PUT',
          url: `${HOST}/invite/${uuid}/firebase`,
          data: {
            firebase_token: payload.firebaseToken
          }
        })

        this.removeMemberInvite(uuid)
        toast({ title: 'Invite accepted' })
      } catch (e) {
        toast({ title: 'Can\'t accept invite, try again.', variant: 'destructive' })
      }
    },

    // --- Projects ------------------------------------------------------------------

    /** Place outlines for the org's projects, keyed by slug path ("ukraine/kyiv"). */
    async fetchPlaceOutlines() {
      const orgId = selectedOrgId()
      if (!orgId) return {}

      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/places/outlines`
        })
        return response.data
      } catch {
        return {}
      }
    },

    async fetchJoinableProjects() {
      const orgId = selectedOrgId()
      if (!orgId) return

      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/joinable`
        })
        this.joinableProjects = response.data
      } catch (e) {
        this.joinableProjects = []
      }
    },

    async joinProject(projectId: ProjectId) {
      const orgId = selectedOrgId()

      await API({
        method: 'POST',
        url: `${HOST}/org/${orgId}/project/${projectId}/join`
      })

      await Promise.all([
        this.fetchProjects(),
        this.fetchJoinableProjects(),
        useAccessStore().refresh(orgId).catch(() => {}),
      ])
    },

    async fetchProjects() {
      this.isLoading = true

      // Get organization ID from selectedOrg or current org
      const orgId = selectedOrgId()

      if (!orgId) {
        this.isLoading = false
        return
      }

      try {
        // Use v2 endpoint with organization_id in path
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project`,
          params: { nested: true }
        })

        this.projects = response.data

        try {
          // Also fetch flattened projects from v2 endpoint
          const response = await API({
            method: 'GET',
            url: `${HOST}/org/${orgId}/project`
          })

          this.flatedProjects = response.data
        } catch (e) {
          toast({ title: 'Can\'t fetch flated projects', variant: 'destructive' })
        }
      } catch (e) {
        console.error(e)
        toast({ title: 'Can\'t fetch projects', variant: 'destructive' })
      } finally {
        this.isLoading = false
      }
    },

    async fetchProject(projectID: ProjectId) {
      const orgId = selectedOrgId()

      if (!orgId) {
        return
      }

      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/${projectID}`,
        })

        this.setCurrent(response.data.project)
        this.currentMember = response.data.current_member
        this.membersCount = response.data.members_count
        this.setComponents(response.data.components)
        this.planetFeatures = JSON.parse(response.data.project.planet_features)
      } catch (e: any) {
        if (!e.response) {
          toast({ title: 'Can\'t fetch project', variant: 'destructive' })
          return
        }
        if (e.response.status === 403) {
          toast({ title: 'You are not member of this project', variant: 'destructive' })
        } else if (e.response.status === 404) {
          toast({ title: 'Project not found', variant: 'destructive' })
        } else {
          toast({ title: 'Can\'t fetch project', variant: 'destructive' })
        }
      }
    },

    async updateProject(project: Record<string, any>) {
      const reqData = {...project}
      const orgId = reqData.orgId || selectedOrgId()

      if (!orgId) {
        return
      }

      try {
        const response = await API({
          method: 'PATCH',
          url: `${HOST}/org/${orgId}/project/${project.project_id}`,
          data: {
            project_name: reqData.name,
            project_desc: reqData.description,
            organization_id: reqData.orgId,
            country: reqData.country || null,
            parent_id: parseInt(reqData.parentId) || null,
            ui: reqData.ui || null,
            emoji: reqData.emoji || null,
            workflow_id: reqData.workflow_id ?? null,
          }
        })

        this.removeProjectLocal(response.data)
        this.addProjectLocal(response.data)

        toast({ title: `Project ${response.data.project_name} updated.` })
      } catch (e) {
        toast({ title: 'Can\'t update project. Try again.', variant: 'destructive' })
      }
    },

    async coverRequest(project: ProjectItem, request: Record<string, any>) {
      const orgId = project.organization_id || useOrgStore().selectedOrg
      const response = await API({
        url: `${HOST}/org/${orgId}/project/${project.project_id}/cover`,
        ...request
      })
      const { project_id: projectId, cover_url, cover_type } = response.data
      const apply = (list: ProjectItem[] | undefined) => (list || []).forEach(p => {
        if (p.project_id === projectId) Object.assign(p, {cover_url, cover_type})
        apply(p.children)
      })
      apply(this.projects)
      apply(this.flatedProjects)
      apply(this.joinableProjects)
      if (this.current?.project_id === projectId) {
        this.current = {...this.current, cover_url, cover_type}
        localStorage.setItem('selectedProject', JSON.stringify(this.current))
      }
    },

    uploadProjectCover({ project, file }: { project: ProjectItem, file: any }) {
      return this.coverRequest(project, {
        method: 'PUT',
        headers: {'Content-Type': 'multipart/form-data'},
        data: {file}
      })
    },

    setProjectCoverType({ project, coverType }: { project: ProjectItem, coverType: string }) {
      return this.coverRequest(project, {
        method: 'PATCH',
        data: {cover_type: coverType}
      })
    },

    removeProjectCover(project: ProjectItem) {
      return this.coverRequest(project, {
        method: 'DELETE'
      })
    },

    unlistProject(project: ProjectItem) {
      this.removeProjectLocal(project)
    },

    async deleteProject(project: ProjectItem) {
      const orgId = selectedOrgId()

      if (!orgId) {
        return
      }

      try {
        await API({
          method: 'DELETE',
          url: `${HOST}/org/${orgId}/project/${project.project_id}`,
        })

        this.removeProjectLocal(project)
        toast({ title: `Project ${project.project_name} removed` })
      } catch (e) {
        toast({ title: 'Can\'t remove project. Try again.', variant: 'destructive' })
      }
    },

    async createProjectRequest(projectData: Record<string, any>) {
      const reqData = {...projectData}

      // Use orgId from request data or fall back to selectedOrg
      const orgId = reqData.orgId || selectedOrgId()

      if (!orgId) {
        return
      }

      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/org/${orgId}/project/create`,
          data: {
            project_name: reqData.name,
            project_desc: reqData.description,
            organization_id: orgId,
            country: reqData.country || null,
            parent_id: reqData.parentId && reqData.parentId !== '' ? parseInt(reqData.parentId) : null,
            ui: reqData.ui || 'Default',
            emoji: reqData.emoji || null,
            workflow_id: reqData.workflow_id ?? null,
          }
        })

        this.addProjectLocal(response.data)
        toast({ title: `${reqData.name} created` })
        return response.data
      } catch (e: any) {
        if (!e.response) {
          toast({ title: 'Can\'t create project. Try again.', variant: 'destructive' })
        }
        if (e.response.status === 402) {
          const errorData = e.response.data.detail
          const message = errorData.message
          toast({ title: message, variant: 'destructive' })
        } else if (e.response.status === 403) {
          toast({ title: 'Not enough permissions', variant: 'destructive' })
        } else if (e.response.status === 400) {
          toast({ title: `${e.response.data.detail}`, variant: 'destructive' })
        } else {
          toast({ title: 'Can\'t create project. Try again.', variant: 'destructive' })
        }
      }
    },

    // --- Tags & components -----------------------------------------------------

    async fetchAllTags() {
      const orgId = selectedOrgId()

      if (!orgId) {
        return
      }

      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/project/tag`
        })
        this.allTags = response.data
      } catch (e: any) {
        throw new Error(e)
      }
    },

    async fetchAllComponents() {
      const orgId = selectedOrgId()

      if (!orgId) {
        toast({ title: 'No organization selected', variant: 'destructive' })
        return
      }

      try {
        const response = await API({
          method: 'GET',
          url: `${HOST}/org/${orgId}/components`
        })

        this.allComponents = response.data
      } catch (e) {
        console.log(e)
      }
    },

    async addComponent(payload: { projectId: ProjectId, name: string }) {
      const orgId = selectedOrgId()

      if (!orgId) {
        toast({ title: 'No organization selected', variant: 'destructive' })
        return
      }

      try {
        const response = await API({
          method: 'POST',
          url: `${HOST}/org/${orgId}/project/${payload.projectId}/components/${payload.name}`
        })

        this.setComponents(response.data)
      } catch (e) {
        toast({ title: "Can't add component", variant: 'destructive' })
      }
    },

    async removeComponent(payload: { projectId: ProjectId, name: string }) {
      const orgId = selectedOrgId()

      if (!orgId) {
        toast({ title: 'No organization selected', variant: 'destructive' })
        return
      }

      try {
        const response = await API({
          method: 'DELETE',
          url: `${HOST}/org/${orgId}/project/${payload.projectId}/components/${payload.name}`
        })

        this.setComponents(response.data)
      } catch (e) {
        toast({ title: "Can't remove component", variant: 'destructive' })
      }
    },
  },
})
